import classNames from 'classnames';

import React from 'react';
import { FormattedMessage } from 'react-intl';

import type { MayBe } from 'modules/global/typing/common/common';

import Checkbox from 'ui/base/Checkbox';

type YesNoValue = boolean | string;

export type YesNoOptions<T extends YesNoValue> = readonly [yes: T, no: T];

export const BOOLEAN_OPTIONS: YesNoOptions<boolean> = [true, false];

type YesNoCheckboxesProps<T extends YesNoValue> = {
  name: string;
  options: YesNoOptions<T>;
  value?: T | null;
  className?: string;
  label?: React.ReactNode;
  onChange?: (value: MayBe<T>) => void;
};

const YesNoCheckboxes = <T extends YesNoValue>({ name, label, value, onChange, className, options: [yesValue, noValue] }: YesNoCheckboxesProps<T>) => {
  const options = React.useMemo(() => [
    {
      value: yesValue,
      disabled: value === noValue,
      label: (
        <span data-cy={`yes-${name}`}>
          <FormattedMessage id="yes" defaultMessage="Yes" />
        </span>
      ),
    },
    {
      value: noValue,
      disabled: value === yesValue,
      label: (
        <span data-cy={`no-${name}`}>
          <FormattedMessage id="no" defaultMessage="No" />
        </span>
      ),
    },
  ], [name, value, yesValue, noValue]);

  const handleOnChange = React.useCallback(
    (values: Array<T>) => {
      onChange?.(values.at(0));
    },
    [onChange]
  );

  return (
    <div className={classNames('d-flex justify-content-between', className)}>
      {label && <div className="w-66">{label}</div>}
      <Checkbox.Group data-cy={name} value={[value]} options={options} onChange={handleOnChange} className="d-flex align-items-center gap-0" />
    </div>
  );
};

export default YesNoCheckboxes;
